import { Auth } from "@aws-amplify/auth";
import type {
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { useCallback } from "react";

import { Logger } from "@/apis/Logger";
import { AIAgentSessionHelper } from "@/helpers/agentcore/ai-agent-session-helper";
import { isElectronApp } from "@/helpers/electron-helper";

import { InvokeAgentOptions } from "../@types/agentcore";

/**
 * Force-refresh the Cognito session using the refresh token.
 * Unlike Auth.currentSession(), this always calls Cognito's token endpoint
 * to obtain brand new tokens, regardless of the current token's validity.
 */
const forceRefreshSession = (
  user: CognitoUser,
  refreshToken: CognitoRefreshToken
): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    user.refreshSession(refreshToken, (err, session) => {
      if (err) {
        reject(err);
      } else {
        resolve(session as CognitoUserSession);
      }
    });
  });
};

/**
 * Refreshes the Cognito ID token and updates it in Electron.
 * Always force-refreshes via user.refreshSession() to guarantee a fresh
 * ID token with the full TTL before starting long-running agent workflows.
 *
 * @returns The new token's expiry in seconds since the epoch, or undefined outside
 * Electron. A caller outliving one token needs it to know when to come back.
 */
export const refreshBearerToken = async (): Promise<number | undefined> => {
  if (!window.electronAPI?.setBearerToken) {
    return undefined; // Not in Electron environment
  }

  const user = (await Auth.currentAuthenticatedUser()) as CognitoUser;
  const session: CognitoUserSession | null = user.getSignInUserSession();

  if (!session) {
    throw new Error("No active user session");
  }

  // Always force-refresh to guarantee a fresh token for the entire workflow.
  // Uses user.refreshSession() directly instead of Auth.currentSession()
  // because the latter returns cached tokens when its internal isValid()
  // check passes (zero-buffer expiry check).
  const refreshToken: CognitoRefreshToken = session.getRefreshToken();
  const newSession = await forceRefreshSession(user, refreshToken);
  const idToken = newSession.getIdToken();
  await window.electronAPI.setBearerToken(idToken.getJwtToken());
  void Logger.info("Bearer token refreshed before agent invocation");
  return idToken.getExpiration();
};

/**
 * Custom hook that provides a safe wrapper for invoking AI agents.
 * Returns a non-async function suitable for event handlers.
 *
 * @param prompt The prompt to send to the agent
 * @param agentType The type of agent to invoke
 * @returns A function that invokes the agent (returns void, not Promise)
 */
export const useInvokeAgent = (
  prompt: string,
  agentType: InvokeAgentOptions["agentType"],
  agentCoreUrl?: string
) => {
  const invokeAgent = useCallback(() => {
    void (async () => {
      try {
        if (!isElectronApp()) {
          void Logger.error(
            `Cannot start ${agentType} automation: Not running in Electron app`
          );
          return;
        }

        try {
          await refreshBearerToken();
        } catch (tokenError) {
          const tokenErrorMessage =
            tokenError instanceof Error
              ? tokenError.message
              : String(tokenError);
          void Logger.error(
            `Failed to refresh bearer token for ${agentType} automation: ${tokenErrorMessage}`
          );
          // Token refresh failed entirely (no authenticated user or no session).
          // Proceed with invocation - AgentCore will fail with 401 if unauthorized.
        }

        const { sessionId, actorId } = AIAgentSessionHelper.createSession();

        const result = await window.electronAPI.invokeAgent(prompt, {
          sessionId,
          actorId,
          agentType,
          ...(agentCoreUrl && { agentCoreUrl }),
        });

        if (!result || !result.success) {
          void Logger.error(
            `Failed to start ${agentType} automation: ${
              result?.error ?? "Unknown error"
            }`
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        void Logger.error(
          `Error starting ${agentType} automation: ${errorMessage}`
        );
      }
    })();
  }, [prompt, agentType, agentCoreUrl]);

  return invokeAgent;
};
